import React from "react";

import { useLiveUpdates } from "@hooks/useLiveUpdates";
import { IdTooltip } from "@src/components/elements/IdTooltip";
import Card from "@src/components/submodules/Card";
import { buildThemeClasses } from "@src/utils/theme";
import { getContentfulPreviewPropsFn } from "@utils";
import clsx from "clsx";
import { graphql } from "gatsby";

export function SectionCardList({ entry: rawEntry }) {
  const entry = useLiveUpdates(rawEntry);
  const getPreviewProps = getContentfulPreviewPropsFn(entry?.contentful_id);
  const cards = entry?.cardsCollection?.items ?? entry?.cards;
  const ctaPreviewProps = getContentfulPreviewPropsFn(
    entry?.cta?.sys?.id ?? entry?.card_list_cta?.contentful_id
  );

  return (
    <section
      className={clsx("font-atkinson py-section", {
        ...buildThemeClasses(entry?.customTheme),
      })}
      id={entry?.contentful_id}
    >
      <IdTooltip id={entry?.contentful_id} />
      <div className="inner-small">
        <div className="max-w-760px mx-auto md:text-center flex flex-col gap-4">
          <h2
            className={clsx("text-rt-xl leading-tight")}
            {...getPreviewProps("headline")}
          >
            {entry?.headline}
          </h2>
          {(entry?.content?.content || entry?.content) && (
            <p className={clsx("md:text-18px")} {...getPreviewProps("content")}>
              {entry?.content?.content ?? entry?.content}
            </p>
          )}
        </div>

        <div
          className={clsx({
            "grid mt-20 gap-12 md:gap-20 md:grid-cols-3": entry.presentation === 'Column',
            "flex mt-20 flex-wrap gap-3 justify-center": entry.presentation === 'Card',
          })}
          {...getPreviewProps("cards")}
        >
          {cards?.map((card) => (
            <Card key={card.sys?.id ?? card.contentful_id} {...card} layout={entry.presentation} />
          ))}
        </div>

        {entry?.card_list_cta && (
          <div
            className="flex justify-center mt-12"
            {...getPreviewProps("cta")}
          >
            <a
              className={clsx("btn-new min-w-200px justify-center")}
              href={entry?.card_list_cta?.url}
              {...ctaPreviewProps("cta")}
            >
              {entry?.cta?.label ?? entry?.card_list_cta?.label}
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

export const SectionCardListFragment = graphql`
  fragment SectionCardList on ContentfulSectionCardList {
    __typename
    id
    contentful_id
    headline
    customTheme
    card_list_cta: cta {
      contentful_id
      __typename
      label
      url
    }
    content {
      content
    }
    presentation
    cards {
      contentful_id
      __typename
      image {
        file {
          url
        }
        description
      }
      headline
      body {
        raw
      }
      cta {
        id
        label
        url
      }
    }
  }
`;
